// apps/web-pwa/src/components/InviteSubmittedScreen.tsx
//
// Renders the "Thanks! Availability received" PWA confirmation screen.
// Direct port of design-samples/pwa-invited-direct.html — do not redesign.
// See .learnings/LEARNINGS.md LRN-20260502-003 for the avatar/crown pattern
// (masked photo inside circle, crown is a sibling SVG outside the clip mask).

import { useState } from 'react';
import organizerPhotoDefault from '../assets/invite-submitted/organizer-photo.png';
import phoneMockup from '../assets/invite-submitted/phone-mockup-text.png';

export type SubmittedSlot = {
  dayLabel: string; // "Tue, May 21"
  timeLabel: string; // "7:30 – 9:00 PM"
  availableCount: number; // 4
  totalCount: number; // 4
};

export interface InviteSubmittedScreenProps {
  eventTitle: string;
  locationName?: string;
  dateRangeLabel: string;
  organizerName: string;
  organizerPhotoUrl?: string;
  submittedSlots: SubmittedSlot[];
  onBack?: () => void;
  onMore?: () => void;
  onDownloadApp?: () => void;
  onDismissPromo?: () => void;
}

const noop = () => {};

// ---- Inline icons (decorative; aria-hidden="true") -------------------------

const CalendarIcon = ({ size = 26 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="17" rx="3" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const PinIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const CrownIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3 8l4 3 5-6 5 6 4-3-2 11H5L3 8z" />
  </svg>
);

const CheckIcon = ({ size = 16, strokeWidth = 3 }: { size?: number; strokeWidth?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SparkleIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2l1.6 5.2L19 8.9l-4.2 3.5L16 18l-4-2.8L8 18l1.2-5.6L5 8.9l5.4-1.7L12 2z" />
  </svg>
);

const PersonIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 20c1.5-3.5 4-5 7-5s5.5 1.5 7 5" />
  </svg>
);

const BellIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 16v-5a6 6 0 1 0-12 0v5l-2 2h16l-2-2z" />
    <path d="M10 20a2 2 0 0 0 4 0" />
  </svg>
);

const LockIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 1 1 8 0v4" />
  </svg>
);

const PeopleAvailIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 19c1.2-3 3.4-4.4 6-4.4s4.8 1.4 6 4.4" />
    <circle cx="17" cy="9" r="2.6" />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const ChatIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V6a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v9z" />
  </svg>
);

const AppleIcon = () => (
  <svg width="11" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 2c.1 1.4-.5 2.7-1.4 3.6-.9 1-2.3 1.7-3.6 1.6-.1-1.4.5-2.7 1.4-3.7C13.7 2.6 15.2 2 16.5 2zM20 17.4c-.6 1.3-.9 1.9-1.7 3.1-1.1 1.6-2.6 3.6-4.5 3.6s-2.4-1.2-5-1.2-3.2 1.2-5 1.2-3.3-1.9-4.4-3.5C-.4 16.4-.7 11 1.5 8.1 3 6 5.4 4.7 7.7 4.7c2 0 3.5 1.2 5 1.2 1.5 0 2.6-1.2 5-1.2 1.8 0 3.7 1 5.1 2.6-4.4 2.4-3.7 8.7-2.8 10.1z" />
  </svg>
);

// ---- Component -------------------------------------------------------------

export function InviteSubmittedScreen({
  eventTitle,
  locationName,
  dateRangeLabel,
  organizerName,
  organizerPhotoUrl,
  submittedSlots,
  onBack = noop,
  onMore = noop,
  onDownloadApp = noop,
  onDismissPromo,
}: InviteSubmittedScreenProps) {
  const [promoDismissed, setPromoDismissed] = useState(false);

  const handleDismissPromo = () => {
    setPromoDismissed(true);
    if (onDismissPromo) onDismissPromo();
  };

  const photoSrc = organizerPhotoUrl ?? organizerPhotoDefault;

  return (
    <div
      data-testid="success-message"
      className="min-h-screen w-full bg-[#FAF8FB] text-[#15131C]"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter", "Helvetica Neue", Arial, sans-serif',
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      <div className="max-w-md mx-auto flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between px-[18px] pt-2 pb-1 h-[52px]">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="w-9 h-9 grid place-items-center rounded-xl text-[#15131C] hover:bg-[#F1EEF7] active:scale-[0.96] transition-transform"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onMore}
            aria-label="More"
            className="w-9 h-9 grid place-items-center rounded-xl hover:bg-[#F1EEF7] active:scale-[0.96] transition-transform"
          >
            <span className="text-[22px] leading-none tracking-[1px] text-[#2C2840]">⋯</span>
          </button>
        </div>

        {/* Scroll content */}
        <div className="px-[18px] pb-7 pt-1">
          {/* Hero */}
          <div className="px-1 pt-1 pb-[14px]" role="status" aria-live="polite">
            <h1 className="m-0 mb-[6px] text-[22px] font-bold leading-[1.18] tracking-[-0.01em]">
              Thanks! Availability received{' '}
              <span className="inline-block translate-y-[1px]" aria-hidden="true">
                🎉
              </span>
            </h1>
            <p className="m-0 text-[14px] text-[#6B6880]">
              We&apos;ll find the best time for everyone.
            </p>
          </div>

          {/* Title card */}
          <section
            aria-label="Catch up summary"
            className="bg-white border border-[#ECEAF1] rounded-[20px] p-4 grid items-center"
            style={{
              gridTemplateColumns: '56px 1fr auto',
              gridTemplateRows: 'auto auto',
              columnGap: 14,
              rowGap: 10,
              boxShadow: '0 6px 18px rgba(54,48,98,.07)',
            }}
          >
            <div
              className="w-14 h-14 rounded-2xl bg-[#EFEBFF] grid place-items-center text-[#5B4DE0]"
              style={{ gridRow: '1 / span 2' }}
              aria-hidden="true"
            >
              <CalendarIcon size={26} />
            </div>
            <div className="min-w-0">
              <h2 className="m-0 mb-1 text-[17px] font-bold leading-[1.22] text-[#15131C]">
                {eventTitle}
              </h2>
              <div className="flex flex-wrap gap-x-[10px] gap-y-1 text-[13px] text-[#6B6880] items-center">
                {locationName && (
                  <span className="inline-flex items-center gap-1">
                    <PinIcon />
                    {locationName}
                  </span>
                )}
                {locationName && <span className="text-[#A6A3B8]">·</span>}
                <span className="inline-flex items-center gap-1">{dateRangeLabel}</span>
              </div>
            </div>
            <div
              className="flex flex-col items-center gap-1 w-[60px]"
              style={{ gridRow: '1 / span 2' }}
            >
              {/* Avatar wrapper — does NOT clip; crown is a sibling outside the mask. */}
              <div className="relative w-11 h-11">
                <div className="rounded-full overflow-hidden w-11 h-11 bg-[#F1EEF7]">
                  <img
                    src={photoSrc}
                    alt=""
                    className="w-full h-full object-cover"
                    style={{ transform: 'scale(1.08)', objectPosition: 'center 30%' }}
                  />
                </div>
                <div
                  className="absolute -top-[5px] -right-[7px] w-[18px] h-[18px] text-[#5B4DE0]"
                  style={{ filter: 'drop-shadow(0 1px 2px rgba(91,77,224,.25))' }}
                  aria-hidden="true"
                >
                  <CrownIcon />
                </div>
              </div>
              <div className="text-[10.5px] text-[#6B6880] text-center leading-[1.1]">
                Organizer
                <b className="block text-[#2C2840] font-semibold">{organizerName}</b>
              </div>
            </div>
          </section>

          {/* Stepper */}
          <section
            aria-label="Progress"
            className="mt-[14px] bg-white border border-[#ECEAF1] rounded-[20px] px-4 pt-[18px] pb-[14px]"
            style={{ boxShadow: '0 6px 18px rgba(54,48,98,.07)' }}
          >
            <h3 className="m-0 mb-[14px] text-[14px] font-semibold text-[#2C2840]">
              What happens next?
            </h3>
            <div className="grid grid-cols-4 relative mb-2">
              {/* Step 1: done */}
              <StepCol state="done" connector="solid" isLast={false}>
                <CheckIcon size={16} strokeWidth={3} />
              </StepCol>
              {/* Step 2: active (sparkle) */}
              <StepCol state="active" connector="dashed" isLast={false}>
                <SparkleIcon size={16} />
              </StepCol>
              {/* Step 3: dashed (person) */}
              <StepCol state="dashed" connector="dashed" isLast={false}>
                <PersonIcon />
              </StepCol>
              {/* Step 4: dashed (bell) — last */}
              <StepCol state="dashed" connector="dashed" isLast={true}>
                <BellIcon />
              </StepCol>
            </div>
            <div className="grid grid-cols-4 gap-x-[6px] mt-1">
              <StepLabel title="Just you" />
              <StepLabel title="Finding the best time" subtitle="In progress" active />
              <StepLabel title="Organizer reviews" subtitle="You'll be notified" />
              <StepLabel title="Time confirmed" />
            </div>
          </section>

          {/* Privacy banner */}
          <div
            role="note"
            className="mt-[14px] bg-[#E5F5EE] border border-[#CDEBDD] rounded-[14px] px-[14px] py-3 flex items-center gap-3"
          >
            <div className="w-7 h-7 rounded-full bg-white grid place-items-center text-[#1FB573] flex-shrink-0">
              <LockIcon size={14} />
            </div>
            <p className="m-0 text-[12.5px] text-[#1A6B45] leading-[1.35]">
              <b className="text-[#0F5733] font-semibold block">Your response is private</b>
              Only the organizer sees your availability. We won&apos;t share your choices with other
              guests.
            </p>
          </div>

          {/* Availability */}
          <h3 className="mt-[18px] mb-[10px] mx-1 text-[14px] font-semibold text-[#2C2840]">
            Your submitted availability
          </h3>
          <div>
            {submittedSlots.length === 0 ? (
              <div className="text-[13px] text-[#6B6880] py-2">No slots submitted.</div>
            ) : (
              submittedSlots.map((slot, i) => (
                <div
                  key={`${slot.dayLabel}-${slot.timeLabel}-${i}`}
                  className="bg-white border border-[#ECEAF1] rounded-2xl px-[14px] py-3 flex items-center gap-3 mb-2"
                  style={{ boxShadow: '0 1px 2px rgba(20,18,40,.04)' }}
                >
                  <div className="w-9 h-9 bg-[#F5F2FF] rounded-[10px] grid place-items-center text-[#5B4DE0] flex-shrink-0">
                    <CalendarIcon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-[#15131C]">{slot.dayLabel}</div>
                    <div className="text-[12.5px] text-[#6B6880] mt-[2px]">{slot.timeLabel}</div>
                  </div>
                  <div className="flex items-center gap-[6px] text-[12px] text-[#6B6880] mr-1 whitespace-nowrap">
                    <span className="text-[#1FB573]">
                      <PeopleAvailIcon />
                    </span>
                    {slot.availableCount} of {slot.totalCount} available
                  </div>
                  <div className="w-[22px] h-[22px] rounded-full bg-[#1FB573] text-white grid place-items-center flex-shrink-0">
                    <CheckIcon size={12} strokeWidth={3} />
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Promo */}
          {!promoDismissed && (
            <section
              aria-label="Download the app"
              className="mt-4 border border-[#ECEAF1] rounded-[20px] px-4 pt-4 pb-[18px] relative overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, #F4F1FF 0%, #FFFFFF 100%)',
                boxShadow: '0 6px 18px rgba(54,48,98,.07)',
              }}
            >
              <button
                type="button"
                onClick={handleDismissPromo}
                aria-label="Dismiss"
                className="absolute top-3 right-3 w-[26px] h-[26px] rounded-full bg-[rgba(20,18,40,0.04)] grid place-items-center text-[#6B6880] hover:bg-[rgba(20,18,40,0.08)]"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </svg>
              </button>
              <div className="grid items-center gap-3" style={{ gridTemplateColumns: '120px 1fr' }}>
                <div
                  className="rounded-2xl overflow-hidden grid place-items-center"
                  style={{ width: 120 }}
                >
                  <img
                    src={phoneMockup}
                    alt=""
                    loading="lazy"
                    className="block w-full h-auto"
                    style={{ imageRendering: 'auto' }}
                  />
                </div>
                <div>
                  <span className="inline-block bg-white border border-[#ECEAF1] text-[#5B4DE0] rounded-full px-[9px] py-[3px] text-[10.5px] font-bold mb-[6px]">
                    New
                  </span>
                  <h2 className="m-0 mb-2 text-[15px] font-bold leading-[1.25] text-[#15131C]">
                    Make connections effortless with the CatchUp app
                  </h2>
                  <ul className="list-none m-0 mb-3 p-0 grid gap-[5px]">
                    <li className="text-[12px] text-[#6B6880] flex items-center gap-[6px]">
                      <span className="text-[#5B4DE0] flex-shrink-0">
                        <ClockIcon />
                      </span>
                      Smart nudges to reach out
                    </li>
                    <li className="text-[12px] text-[#6B6880] flex items-center gap-[6px]">
                      <span className="text-[#5B4DE0] flex-shrink-0">
                        <CheckIcon size={13} strokeWidth={2.2} />
                      </span>
                      Track your connection health
                    </li>
                    <li className="text-[12px] text-[#6B6880] flex items-center gap-[6px]">
                      <span className="text-[#5B4DE0] flex-shrink-0">
                        <ChatIcon />
                      </span>
                      One-tap invites &amp; instant scheduling
                    </li>
                  </ul>
                </div>
              </div>
              <button
                type="button"
                onClick={onDownloadApp}
                className="block w-full bg-[#5B4DE0] text-white rounded-xl px-[14px] py-3 text-[14px] font-semibold mt-[6px] hover:shadow-[0_14px_30px_rgba(91,77,224,0.38)] active:translate-y-[1px] active:scale-[0.995] transition-transform"
                style={{ boxShadow: '0 12px 26px rgba(91,77,224,.30)' }}
              >
                Download CatchUp
              </button>
              <div className="mt-2 text-center text-[11.5px] text-[#6B6880] flex items-center justify-center gap-1">
                <AppleIcon />
                Free on iOS
              </div>
            </section>
          )}

          {/* Notify line */}
          <div className="mt-[14px] flex gap-[10px] items-start px-1">
            <div className="w-6 h-6 rounded-[7px] bg-[#F5F2FF] grid place-items-center text-[#5B4DE0] flex-shrink-0">
              <SparkleIcon size={14} />
            </div>
            <p className="m-0 text-[12px] text-[#6B6880] leading-[1.4]">
              We&apos;ll notify you here as soon as a time is confirmed — no need to check back.
              Want updates on the go? The CatchUp app keeps everyone in sync.
            </p>
          </div>

          {/* Footer */}
          <div className="mt-[18px] pt-[14px] border-t border-[#ECEAF1] flex justify-center items-center gap-2 text-[11.5px] text-[#6B6880]">
            <span className="text-[#A6A3B8] inline-flex" aria-hidden="true">
              <LockIcon size={11} />
            </span>
            Secure
            <span className="w-[3px] h-[3px] bg-[#A6A3B8] rounded-full" aria-hidden="true" />
            No account needed
            <span className="w-[3px] h-[3px] bg-[#A6A3B8] rounded-full" aria-hidden="true" />
            Powered by <b className="text-[#5B4DE0] font-semibold">CatchUp</b>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Stepper helpers -------------------------------------------------------

type StepState = 'done' | 'active' | 'dashed';
type ConnectorStyle = 'solid' | 'dashed';

function StepCol({
  state,
  connector,
  isLast,
  children,
}: {
  state: StepState;
  connector: ConnectorStyle;
  isLast: boolean;
  children: React.ReactNode;
}) {
  const dotBase =
    'w-9 h-9 rounded-full grid place-items-center relative z-[2]';
  let dotClass = '';
  let dotStyle: React.CSSProperties = {};
  if (state === 'done') {
    dotClass = `${dotBase} bg-[#5B4DE0] text-white`;
  } else if (state === 'active') {
    dotClass = `${dotBase} bg-[#5B4DE0] text-white`;
    dotStyle = { boxShadow: '0 0 0 6px rgba(91,77,224,0.10)' };
  } else {
    dotClass = `${dotBase} bg-white text-[#A6A3B8]`;
    dotStyle = { border: '1.5px dashed #A6A3B8' };
  }

  // Connector line (between this dot and next)
  let lineStyle: React.CSSProperties = {
    position: 'absolute',
    top: 18,
    left: '50%',
    right: '-50%',
    height: 2,
    zIndex: 1,
  };
  if (connector === 'solid' && state === 'done') {
    lineStyle = { ...lineStyle, background: '#5B4DE0', opacity: 1 };
  } else {
    // dashed gray
    lineStyle = {
      ...lineStyle,
      backgroundImage: 'linear-gradient(to right, #A6A3B8 50%, transparent 0%)',
      backgroundSize: '6px 2px',
      backgroundRepeat: 'repeat-x',
      backgroundColor: 'transparent',
      opacity: 0.55,
    };
  }

  return (
    <div className="flex flex-col items-center relative text-center">
      <div className={dotClass} style={dotStyle} aria-hidden="true">
        {children}
      </div>
      {!isLast && <div style={lineStyle} aria-hidden="true" />}
    </div>
  );
}

function StepLabel({
  title,
  subtitle,
  active = false,
}: {
  title: string;
  subtitle?: string;
  active?: boolean;
}) {
  return (
    <div className="text-center text-[11.5px] leading-[1.25] text-[#6B6880]">
      <b
        className={`block font-semibold text-[12px] mb-[2px] ${
          active ? 'text-[#5B4DE0]' : 'text-[#2C2840]'
        }`}
      >
        {title}
      </b>
      {subtitle}
    </div>
  );
}

export default InviteSubmittedScreen;
